import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import {
  IPaginatedFeedMessagesQueryParams,
  IPaginatedMessageCommentsQueryParams,
} from './interfaces';
import {
  deleteComment,
  deleteCommentLike,
  deleteFeedMessage,
  deleteLike,
  getFeedMessages,
  getMessageComments,
  postComment,
  postCommentLike,
  postFeedMessage,
  postLike,
} from '@api/feeds';
import { AxiosError } from 'axios';
import {
  ICommentParams,
  IPostFeedMessageParams,
  ResourceType,
} from '@api/feeds/interfaces';

export const useGetFeedMessages = ({
  resourceType,
  resourceId,
  page,
  pageSize,
}: IPaginatedFeedMessagesQueryParams) => {
  return useQuery({
    queryKey: [
      'resource-feed-messages',
      resourceType,
      resourceId,
      page,
      pageSize,
    ],
    queryFn: () => {
      return getFeedMessages(resourceType, resourceId, page + 1, pageSize);
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const usePostFeedMesageMutation = () => {
  return useMutation<
    null,
    AxiosError,
    {
      resourceType: ResourceType;
      resourceId: number | string;
      feedMessageParams: IPostFeedMessageParams;
    }
  >({
    mutationFn: async ({ resourceType, resourceId, feedMessageParams }) =>
      postFeedMessage(resourceType, resourceId, feedMessageParams),
  });
};

export const useDeleteFeedMessage = () => {
  return useMutation<void, AxiosError, number>({
    mutationFn: deleteFeedMessage,
  });
};

export const useGetMessageComments = (
  { messageId, fromDate, page, pageSize }: IPaginatedMessageCommentsQueryParams,
  options?: Partial<UseQueryOptions>
) => {
  return useQuery({
    queryKey: ['message', messageId, 'comments', fromDate, page, pageSize],
    queryFn: () => {
      return getMessageComments(messageId, fromDate, page + 1, pageSize);
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...options,
  });
};

export const usePostCommentMutation = () => {
  return useMutation<
    null,
    AxiosError,
    {
      messageId: number;
      commentParams: ICommentParams;
    }
  >({
    mutationFn: async ({ messageId, commentParams }) =>
      postComment(messageId, commentParams),
  });
};

export const useDeleteComment = () => {
  return useMutation<
    void,
    AxiosError,
    { messageId: number; commentId: number }
  >({
    mutationFn: async ({ messageId, commentId }) =>
      deleteComment(messageId, commentId),
  });
};

export const usePostLikeMutation = () => {
  return useMutation<void, AxiosError, number>({
    mutationFn: postLike,
  });
};

export const useDeleteLikeMutation = () => {
  return useMutation<void, AxiosError, number>({
    mutationFn: deleteLike,
  });
};

export const usePostCommentLikeMutation = () => {
  return useMutation<
    void,
    AxiosError,
    { messageId: number; commentId: number }
  >({
    mutationFn: async ({ messageId, commentId }) =>
      postCommentLike(messageId, commentId),
  });
};

export const useDeleteCommentLikeMutation = () => {
  return useMutation<
    void,
    AxiosError,
    { messageId: number; commentId: number }
  >({
    mutationFn: async ({ messageId, commentId }) =>
      deleteCommentLike(messageId, commentId),
  });
};
